import React, { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { leagueData } from '../types/types';
import axiosInstance from '../axiosConfig';
import { useGlobalStore } from '../store/globalStore';
import { Link, useNavigate } from 'react-router-dom';
import { useClubStore } from '../store/clubStore';
import { useTournamentStore } from '../store/tournamentStore';
import { formatDate } from '../utils/utils';

import NotFind from '../components/customLayout/notfind';
import SyncPlayerBtn from '../components/customLayout/SyncPlayerBtn';
import tournamentsSearchState from '../assets/images/tournaments-item-search.svg';
import pdfIcon from '../assets/images/pdfIcon.svg';

const LeaguesPage: React.FC = () => {
  const isLoading = useGlobalStore((state) => state.isLoading);
  const setLoading = useGlobalStore((state) => state.setLoading);
  const myClub = useClubStore((state) => state.myClub);
  const resetTournament = useTournamentStore((state) => state.resetTournament);
  const navigate = useNavigate();
  const [data, setData] = useState<Array<leagueData>>([]);

  const generateDocFileUrl = (docFile: string) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    return `${import.meta.env.VITE_APP_BASE_URL}${docFile}?time=${new Date().getTime()}`;
  };

  const tableHeader = [
    { key: 'team', text: '🏌️Mannschaft' },
    { key: 'appointment', text: '📅 Termin' },
    { key: 'report', text: '📢 Meldung' },
  ];

  const getList = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('league/list');
      setData(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error logging in:', error);
    }
  };

  const formatFinalRegistrationDate = (dateString: string): string => {
    if (!dateString) return '';
    const [datePart, timePart] = dateString.split(' ');
    const [year, month, day] = datePart.split('-');
    return `${day}.${month}.${year.slice(2)} ${timePart}`;
  };

  const generateTournament = async (
    ligaId: number,
    classId: number,
    teamId: number,
    clubId: number,
    date: string,
    secondTeam: boolean,
    registerId: number,
    finalRegistrationDate: string,
  ) => {
    try {
      setLoading(true);
      const response = await axiosInstance.post('league/generateTournament', {
        id: ligaId,
        class_id: classId,
        team_id: teamId,
        club_id: clubId,
        second_team: secondTeam,
        register_id: registerId,
        date,
      });
      navigate(
        `/tournaments/${response.data.tournament_id}?deadline=${finalRegistrationDate}`,
      );
      setLoading(false);
    } catch (error) {
      console.error('Error logging in:', error);
    }
  };

  useEffect(() => {
    getList();
  }, []);

  return (
    <div className={`grid gap-10 px-5`}>
      <div className="border bg-white p-4 grid gap-2 border-neutral-100 hover:shadow hover:border-neutral-300 transition rounded-sm">
        <div className="font-medium text-lg mb-4">Ligen </div>
        {data.length === 0 ? (
          <div className="py-10">
            {isLoading ? (
              <div className="text-center">
                <img
                  className="mx-auto py-10 animate-bounce"
                  alt="tournaments not find "
                  src={tournamentsSearchState}
                />
                <div className="text-2xl">Bitte warten Sie.</div>
                <div>
                  {' '}
                  Wir durchsuchen die Datenbank, um die Liste für Sie
                  anzuzeigen. Bitte warten Sie...
                </div>
              </div>
            ) : (
              <NotFind />
            )}
          </div>
        ) : (
          <>
            {data.map((item, index) => (
              <div key={index}>
                <div className="bg-gradient-to-r from-[#52A445] to-[#105D1F] text-white text-lg font-medium p-2 md:sticky md:top-16 z-20">
                  {item.title}
                </div>
                {item.classes.map((classItem, key) => (
                  <div key={key}>
                    <div className="border bg-green-50 text-lg border-neutral-300 font-medium p-2 md:sticky md:top-[100px]  z-10 shadow-sm">
                      ⛳ {classItem.title}
                    </div>
                    <div className="my-1">
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              {tableHeader.map((header, key) => (
                                <TableHead
                                  className={`whitespace-nowrap ${header.key === 'team' ? 'w-1/3' : 'w-1/4'}`}
                                  key={key}
                                >
                                  {header.text}
                                </TableHead>
                              ))}
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {classItem.tournaments.map((tournament, index) => (
                              <TableRow key={index}>
                                <TableCell className="whitespace-nowrap font-medium">
                                  {tournament.anzeigename}
                                </TableCell>
                                <TableCell>
                                  {tournament.heimspiel ? (
                                    <>
                                      {formatDate(
                                        tournament.heimspiel.date.split(' ')[0],
                                      )}
                                    </>
                                  ) : (
                                    '...'
                                  )}
                                </TableCell>
                                <TableCell>
                                  <div onClick={() => resetTournament()}>
                                    <div>
                                      {tournament.doc_file && (
                                        <div className="flex text-[#52A445] hover:underline  items-center text-center">
                                          <img
                                            width="20px"
                                            src={pdfIcon}
                                            alt="pdf"
                                          />
                                          <a
                                            target="_blank"
                                            rel="noreferrer"
                                            href={generateDocFileUrl(
                                              tournament.doc_file,
                                            )}
                                          >
                                            Anhang anzeigen
                                          </a>
                                        </div>
                                      )}
                                    </div>

                                    {!tournament.heimspiel &&
                                      tournament.club_id === myClub?.id && (
                                        <div className="text-gray-400">
                                          🗓️ Kein Heimspiel
                                        </div>
                                      )}

                                    {tournament.heimspiel && (
                                      <>
                                        {/* =============== Meldung abgelaufen ===============  */}
                                        {new Date(
                                          tournament.heimspiel.date,
                                        ).getTime() /
                                          1000 <=
                                          item.meldung_ab +
                                            Math.floor(Date.now() / 1000) && (
                                          <>
                                            {!classItem.only_result && (
                                              <>
                                                {tournament.is_registration_active ? (
                                                  <div
                                                    onClick={() =>
                                                      generateTournament(
                                                        item.id,
                                                        classItem.id,
                                                        tournament.team_id,
                                                        tournament.club_id,
                                                        tournament.heimspiel.date.split(
                                                          ' ',
                                                        )[0],
                                                        tournament.zweites_team,
                                                        tournament.anmeldung_id,
                                                        formatFinalRegistrationDate(
                                                          tournament.final_registration_date,
                                                        ),
                                                      )
                                                    }
                                                    className="text-[#52A445] flex gap-0.5 items-center hover:underline cursor-pointer"
                                                  >
                                                    <svg
                                                      xmlns="http://www.w3.org/2000/svg"
                                                      fill="none"
                                                      viewBox="0 0 24 24"
                                                      strokeWidth={3}
                                                      stroke="currentColor"
                                                      className="size-3"
                                                    >
                                                      <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        d="m8.25 4.5 7.5 7.5-7.5 7.5"
                                                      />
                                                    </svg>
                                                    {formatFinalRegistrationDate(
                                                      tournament.final_registration_date,
                                                    )}{' '}
                                                    Uhr*
                                                  </div>
                                                ) : (
                                                  <div className="cursor-not-allowed text-gray-400">
                                                    🔒{' '}
                                                    {formatFinalRegistrationDate(
                                                      tournament.final_registration_date,
                                                    )}{' '}
                                                    Uhr*
                                                  </div>
                                                )}
                                              </>
                                            )}

                                            {tournament.club_id ===
                                              myClub?.id && (
                                              <>
                                                {tournament.is_result_active ? (
                                                  <div>
                                                    <Link
                                                      className="text-[#52A445] flex items-center gap-0.5 hover:underline"
                                                      to={`/league/result-submission/${classItem.lig_id}/${tournament.klassen_id}/${tournament.team_id}/${tournament.anmeldung_id}/${tournament.day}`}
                                                    >
                                                      <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        strokeWidth={3}
                                                        stroke="currentColor"
                                                        className="size-3"
                                                      >
                                                        <path
                                                          strokeLinecap="round"
                                                          strokeLinejoin="round"
                                                          d="m8.25 4.5 7.5 7.5-7.5 7.5"
                                                        />
                                                      </svg>
                                                      Ergebniseingabe
                                                    </Link>
                                                  </div>
                                                ) : (
                                                  <SyncPlayerBtn
                                                    id={tournament.anmeldung_id}
                                                    name={
                                                      tournament.anzeigename
                                                    }
                                                  />
                                                )}
                                              </>
                                            )}
                                          </>
                                        )}
                                        {/* =============== Meldung abgelaufen ===============  */}
                                      </>
                                    )}
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export default LeaguesPage;
